import { useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { api360 } from "./api";
import {
  CustomerInfoResponse,
  InsuranceResponse,
  InsuranceTransactionResponse,
  RedemptionHistoryResponse,
} from "../models/Customer";
import { usePermissionStore } from "../stores/permissionStore";

export type ErrorType<Error> = AxiosError<Error>;

export interface ErrorResponse {
  errors?: Error[] | null;
}

export type SearchRequest = {
  omneId?: string;
  omneName?: string;
  dob?: string;
  phoneNumber?: string;
  email?: string;
  policyNumber?: string;
  insuranceCountry?: string;
  signUpCountry?: string;
  pagination: {
    currentPage: number;
    pageSize?: number;
  };
  permission_Token?: string;
};

interface SearchReq {
  permission_Token?: string;
}

interface addUserRewardReq {
  userId: string;
  activityTypeId: string;
  numberOfPoint: number;
  remark: string;
}

interface deductUserRewardReq {
  userId: string;
  rewardId: string;
  numberOfPoint: number;
}

interface updateUserRewardReq {
  UserRewardId: string;
  UserId: string;
  RewardPoints: number;
}

interface updateUserRedemptionReq {
  UserRedemptionId: string;
  UserId: string;
  RedemptionPoints: number;
}

export const getSearchCustomer = (
  data: SearchRequest,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/customer/search-customer";
  return api360.post(url, data);
};

export const useSearchCustomer = (data: SearchRequest, enabled: boolean) => {
  return useQuery({
    queryKey: ["searchCustomer", data],
    queryFn: () => getSearchCustomer(data),
    enabled,
    retry: false,
  });
};

export const unlockUser = (omneId: string): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/customer/unlock-user/:omneId".replace(
    ":omneId",
    omneId,
  );
  return api360.post(url, {
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  });
};

export const getCustomerInfo = (
  omneId: string,
): Promise<CustomerInfoResponse> => {
  const url = "/c360/v1/api/customer/customer-info/:omneId".replace(
    ":omneId",
    omneId,
  );
  return api360.post(url, {
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  });
};

export const useGetCustomerInfo = (omneId: string) => {
  return useQuery<CustomerInfoResponse>(
    ["customerInfo", omneId],
    () => getCustomerInfo(omneId),
    {
      enabled: true,
    },
  );
};

export const getRedemptionHistory = (
  data: SearchRequest,
): Promise<RedemptionHistoryResponse> => {
  const url = "/c360/v1/api/customer/wallet-redemption-history";
  return api360.post(url, data);
};

export const useGetRedemptionHistory = (data: SearchRequest) => {
  return useQuery<RedemptionHistoryResponse>(
    ["redemptionHistory", data],
    () => getRedemptionHistory(data),
    {
      enabled: true,
      retry: false,
    },
  );
};

// Get Activity Types for User Rewards
export const getActivityTypes = (data: SearchReq) => {
  const url = "/c360/v1/api/GetActivityTypes";
  return api360.post(url, data);
};
export const useGetActivityTypes = (data: SearchReq) => {
  return useQuery(["activityTypes", data], () => getActivityTypes(data), {
    enabled: true,
    retry: false,
  });
};

// Get Reward Store Types for User Redemptions
export const getRewardStoreType = (data: SearchReq) => {
  const url = "/c360/v1/api/RewardStoreType";
  return api360.post(url, data);
};
export const useGetRewardStoreType = (data: SearchReq) => {
  return useQuery(["rewardStoreType", data], () => getRewardStoreType(data), {
    enabled: true,
    retry: false,
  });
};

// Add User Reward Points
export const addUserReward = (data: addUserRewardReq) => {
  const url = "/c360/v1/api/reward/add-user-reward";
  return api360.post(url, data);
};

// Deduct User Reward Points by adding User Redemptions
export const deductUserReward = (data: deductUserRewardReq) => {
  const url = "/c360/v1/api/reward/deduct-user-reward";
  return api360.post(url, data);
};

// Update User Reward record
export const updateUserReward = (data: updateUserRewardReq) => {
  const url = "/c360/v1/api/customer/update-user-reward";
  return api360.post(url, data);
};

// Update User Redemption record
export const updateUserRedemption = (data: updateUserRedemptionReq) => {
  const url = "/c360/v1/api/customer/update-user-redemption";
  return api360.post(url, data);
};

export const getInsuranceInfo = (
  data: SearchRequest,
): Promise<InsuranceResponse> => {
  const url = "/c360/v1/api/customer/insurance-info";
  return api360.post(url, data);
};

export const useGetInsuranceInfo = (data: SearchRequest) => {
  return useQuery<InsuranceResponse>(
    ["insuranceInfo", data],
    () => getInsuranceInfo(data),
    {
      enabled: true,
      retry: false,
    },
  );
};

export const getInsuranceTransaction = (
  data: SearchRequest,
): Promise<InsuranceTransactionResponse> => {
  const url = "/c360/v1/api/customer/insurance-transaction-information";
  return api360.post(url, data);
};

export const useGetInsuranceTransaction = (data: SearchRequest) => {
  return useQuery<InsuranceTransactionResponse>(
    ["insuranceTransaction", data],
    () => getInsuranceTransaction(data),
    {
      enabled: true,
      retry: false,
    },
  );
};
